import type { NavigatePayload } from '@shared/lib/api/navigation';
import { apiFetch } from './api';
import { audit } from './audit';

export async function navigateToWaypoint(payload: NavigatePayload, destination?: string): Promise<void> {
  if (destination) audit({ type: 'dispatched_robot', destination });
  await apiFetch('/user/navigation/navigate', { method: 'POST', body: JSON.stringify(payload) });
}

export async function emergencyStop(): Promise<void> {
  audit({ type: 'e_stop_pressed', e_stop_type: 'electronic' });
  await apiFetch('/all/estop', { method: 'POST' });
}

export async function clearEstop(): Promise<void> {
  await apiFetch('/all/clear_estop', { method: 'POST' });
}

// Tracks when the current task started so cancelNavigation can emit a
// task_completed event with the elapsed cycle time. The user-facing "stop
// task" button calls cancelNavigation, which we treat as task completion —
// there is no separate complete/abort distinction in the UI today.
let taskStartedAtMs: number | null = null;

export async function startNavigation(): Promise<void> {
  taskStartedAtMs = Date.now();
  audit({ type: 'started_task' });
  await apiFetch('/user/transition_to_navigation', { method: 'POST' });
}

export async function cancelNavigation(): Promise<void> {
  if (taskStartedAtMs != null) {
    const cycleTimeSeconds = Math.max(0, Math.round((Date.now() - taskStartedAtMs) / 1000));
    audit({ type: 'task_completed', cycle_time_seconds: cycleTimeSeconds });
    taskStartedAtMs = null;
  }
  await apiFetch('/all/cancel_navigation', { method: 'POST' });
}

export async function teleopJog(user: string, vx: number, vy: number, vw: number): Promise<void> {
  await apiFetch('/user/navigation/teleop', { method: 'POST', body: JSON.stringify({ user, vx, vy, vw }) });
}

export async function startTeleop(user: string): Promise<void> {
  audit({ type: 'started_driving' });
  await apiFetch('/user/navigation/start_teleop', {
    method: 'POST',
    body: JSON.stringify({ user }),
  });
}

export async function endTeleop(user: string): Promise<void> {
  await apiFetch('/user/navigation/end_teleop', {
    method: 'POST',
    body: JSON.stringify({ user }),
  });
}

export async function undock(): Promise<void> {
  await apiFetch('/user/navigation/undock', { method: 'POST' });
}

export async function setInitialPose(payload: { x: number; y: number; theta: number }): Promise<void> {
  await apiFetch('/all/set_initial_pose', { method: 'POST', body: JSON.stringify(payload) });
}
